/**
 * ゲーム状態管理ストア
 *
 * Zustand を使用したグローバル状態管理
 */

import { create } from 'zustand';
import type { GameState, GameConfig, Action } from '@/types/game';
import { gameApi, handleApiError } from '@services/api/gameApi';
import { socketClient } from '@services/websocket/socketClient';

/**
 * ゲームストア インターフェース
 */
interface GameStore {
  // === State ===
  gameState: GameState | null;
  gameId: string | null;
  humanPlayerPosition: number;
  isLoading: boolean;
  error: string | null;

  // === Actions ===
  createGame: (config: GameConfig) => Promise<void>;
  executeAction: (action: Action) => Promise<void>;
  startNextRound: () => void;
  redistributionDecision: (redistribute: boolean) => void;
  updateGameState: (gameState: GameState) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  resetGame: () => void;
}

/**
 * ゲームストア作成
 */
export const useGameStore = create<GameStore>((set, get) => ({
  // === Initial State ===
  gameState: null,
  gameId: null,
  humanPlayerPosition: 0,
  isLoading: false,
  error: null,

  // === Actions ===

  /**
   * 新規ゲーム作成
   *
   * @param config ゲーム作成設定
   */
  createGame: async (config: GameConfig) => {
    set({ isLoading: true, error: null });

    try {
      // REST APIでゲーム作成
      const response = await gameApi.createGame(config);

      set({
        gameId: response.game_id,
        gameState: response.game_state,
        humanPlayerPosition: config.human_player_position,
        isLoading: false,
      });

      // WebSocket接続してゲーム参加
      socketClient.connect();
      socketClient.joinGame(response.game_id, config.human_player_position);

      console.log('✅ ゲーム作成成功:', response.game_id);
    } catch (error) {
      const errorMessage = handleApiError(error);
      set({ error: errorMessage, isLoading: false });
      console.error('❌ ゲーム作成エラー:', errorMessage);
    }
  },

  /**
   * アクション実行
   *
   * @param action アクション情報
   */
  executeAction: async (action: Action) => {
    const { gameId } = get();
    if (!gameId) {
      set({ error: 'ゲームIDが設定されていません' });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // WebSocket経由でアクション実行
      // （リアルタイム通信のため、レスポンスはイベントで受信）
      socketClient.executeAction(gameId, action);

      set({ isLoading: false });
    } catch (error) {
      const errorMessage = handleApiError(error);
      set({ error: errorMessage, isLoading: false });
      console.error('❌ アクション実行エラー:', errorMessage);
    }
  },

  /**
   * 次のラウンド開始
   */
  startNextRound: () => {
    const { gameId } = get();
    if (!gameId) {
      set({ error: 'ゲームIDが設定されていません' });
      return;
    }

    // WebSocket経由で次のラウンド開始リクエスト
    socketClient.startNextRound(gameId);
    console.log('🔄 次のラウンド開始リクエスト送信');
  },

  /**
   * 5し配り直し選択
   *
   * @param redistribute true=配り直す, false=続行
   */
  redistributionDecision: (redistribute: boolean) => {
    const { gameId } = get();
    if (!gameId) {
      set({ error: 'ゲームIDが設定されていません' });
      return;
    }

    // WebSocket経由で配り直し選択を送信
    socketClient.redistributionDecision(gameId, redistribute);
    console.log(`🔄 配り直し選択: ${redistribute ? '配り直す' : '続行'}`);
  },

  /**
   * ゲーム状態更新（WebSocketイベント受信時）
   *
   * @param gameState 更新後のゲーム状態
   */
  updateGameState: (gameState: GameState) => {
    set({ gameState });
  },

  /**
   * ローディング状態設定
   *
   * @param isLoading ローディング状態
   */
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  /**
   * エラー設定
   *
   * @param error エラーメッセージ
   */
  setError: (error: string | null) => {
    set({ error });
  },

  /**
   * ゲームリセット
   */
  resetGame: () => {
    socketClient.disconnect();
    set({
      gameState: null,
      gameId: null,
      humanPlayerPosition: 0,
      isLoading: false,
      error: null,
    });
  },
}));
